import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Star, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import apiService from '../../services/apiService';

/* ── 애니메이션 ── */
const fadeIn = keyframes`from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); }`;
const spin   = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const SpinAnim = styled.span`
  display: inline-flex;
  ${p => p.$active && css`animation: ${spin} 1s linear infinite;`}
`;

/* ── 레이아웃 ── */
const Page = styled.div`
  min-height: 100vh; background: #0a0a0f; color: #e2e8f0;
  font-family: 'Inter', 'Pretendard', sans-serif; padding: 20px;
`;
const TopBar = styled.div`
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 24px; padding-bottom: 20px; border-bottom: 1px solid #1e2030; flex-wrap: wrap;
`;
const BackBtn = styled.button`
  background: none; border: none; color: #64748b; cursor: pointer; padding: 8px; border-radius: 8px;
  &:hover { background: #1e2030; color: #e2e8f0; }
`;
const TitleArea = styled.div`flex: 1; min-width: 200px;`;
const PageTitle = styled.h1`
  font-size: 22px; font-weight: 800; margin: 0;
  background: linear-gradient(135deg, #a78bfa, #7c3aed);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
`;
const PageSub = styled.p`margin: 2px 0 0; font-size: 12px; color: #64748b;`;

const RunBtn = styled.button`
  display: flex; align-items: center; gap: 7px;
  padding: 10px 20px; border-radius: 10px; font-size: 14px; font-weight: 700;
  cursor: pointer; border: 1px solid #7c3aed;
  background: rgba(124,58,237,0.15); color: #a78bfa;
  transition: all 0.2s;
  &:hover { background: rgba(124,58,237,0.3); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

/* ── 메타 정보 바 ── */
const MetaBar = styled.div`
  display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
  margin-bottom: 20px; padding: 12px 16px;
  background: #111827; border: 1px solid #1e2030; border-radius: 12px;
  font-size: 12px; color: #64748b;
`;
const MetaItem = styled.span`
  display: flex; align-items: center; gap: 5px;
`;
const MetaVal = styled.span`color: #94a3b8; font-weight: 600;`;

/* ── 진행 바 ── */
const ProgressSection = styled.div`
  margin-bottom: 20px; padding: 16px;
  background: #111827; border: 1px solid #1e2030; border-radius: 12px;
`;
const ProgressLabel = styled.div`font-size: 12px; color: #94a3b8; margin-bottom: 8px;`;
const ProgressBar = styled.div`
  height: 6px; background: #1e2030; border-radius: 3px; overflow: hidden;
`;
const ProgressFill = styled.div`
  height: 100%; border-radius: 3px; transition: width 0.3s ease;
  width: ${p => p.$pct}%;
  background: linear-gradient(90deg, #7c3aed, #a78bfa);
`;
const ProgressStock = styled.div`font-size: 11px; color: #64748b; margin-top: 6px;`;

/* ── 카드 그리드 ── */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 16px;
`;

/* ── 종목 카드 ── */
const RANK_COLORS = ['#ffd700', '#c0c0c0', '#cd7f32'];
const getRankColor = (rank) => RANK_COLORS[rank - 1] || '#475569';

const Card = styled.div`
  background: #111827;
  border: 1px solid ${p => p.$rank <= 3 ? getRankColor(p.$rank) + '55' : '#1e2030'};
  border-radius: 16px; padding: 18px;
  animation: ${fadeIn} 0.35s ease;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:hover {
    border-color: ${p => p.$rank <= 3 ? getRankColor(p.$rank) + 'aa' : '#374151'};
    box-shadow: 0 4px 24px rgba(124,58,237,0.1);
  }
`;

const CardTop = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 14px;
`;
const RankBadge = styled.div`
  width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 800; flex-shrink: 0;
  background: ${p => getRankColor(p.$rank)}22;
  color: ${p => getRankColor(p.$rank)};
  border: 1.5px solid ${p => getRankColor(p.$rank)}66;
`;
const StockInfo = styled.div`flex: 1; margin: 0 10px;`;
const StockName = styled.div`font-size: 15px; font-weight: 800; color: #e2e8f0;`;
const StockMeta = styled.div`display: flex; align-items: center; gap: 6px; margin-top: 3px;`;
const StockCode = styled.span`font-size: 10px; color: #475569;`;
const MarketTag = styled.span`
  font-size: 9px; font-weight: 700; padding: 1px 5px; border-radius: 4px;
  background: ${p => p.$market === 'KOSPI' ? 'rgba(96,165,250,0.12)' : 'rgba(52,211,153,0.12)'};
  color:      ${p => p.$market === 'KOSPI' ? '#60a5fa'               : '#34d399'};
`;

/* ── 종합 점수 ── */
const ScoreCircle = styled.div`
  width: 52px; height: 52px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center; flex-direction: column;
  background: ${p =>
    p.$score >= 70 ? 'rgba(16,185,129,0.12)' :
    p.$score >= 50 ? 'rgba(245,158,11,0.12)' :
    'rgba(100,116,139,0.12)'};
  border: 2px solid ${p =>
    p.$score >= 70 ? '#10b981' :
    p.$score >= 50 ? '#f59e0b' :
    '#374151'};
`;
const ScoreNum = styled.div`
  font-size: 16px; font-weight: 900; line-height: 1;
  color: ${p =>
    p.$score >= 70 ? '#10b981' :
    p.$score >= 50 ? '#f59e0b' :
    '#64748b'};
`;
const ScoreLabel = styled.div`font-size: 8px; color: #64748b; font-weight: 600; margin-top: 1px;`;

/* ── 서브 스코어 바 ── */
const SubScores = styled.div`display: flex; flex-direction: column; gap: 7px; margin-bottom: 14px;`;
const ScoreRow = styled.div`display: flex; align-items: center; gap: 8px;`;
const ScoreName = styled.span`font-size: 10px; color: #64748b; width: 64px; flex-shrink: 0;`;
const ScoreBar = styled.div`
  flex: 1; height: 5px; background: #1e2030; border-radius: 3px; overflow: hidden;
`;
const ScoreBarFill = styled.div`
  height: 100%; border-radius: 3px; transition: width 0.5s ease;
  width: ${p => p.$pct}%;
  background: ${p =>
    p.$color === 'dtl'       ? '#60a5fa' :
    p.$color === 'livermore' ? '#f59e0b' :
    p.$color === 'minervini' ? '#a78bfa' :
    '#34d399'};
`;
const ScoreVal = styled.span`font-size: 10px; font-weight: 700; color: #94a3b8; width: 24px; text-align: right;`;

/* ── 태그 ── */
const TagRow = styled.div`display: flex; gap: 5px; flex-wrap: wrap; margin-bottom: 12px;`;
const Tag = styled.span`
  font-size: 9px; font-weight: 700; padding: 2px 7px; border-radius: 99px;
  background: ${p =>
    p.$tag === 'DTL'       ? 'rgba(96,165,250,0.12)'  :
    p.$tag === 'Livermore' ? 'rgba(245,158,11,0.12)'  :
    p.$tag === 'Minervini' ? 'rgba(167,139,250,0.12)' :
    'rgba(52,211,153,0.12)'};
  color: ${p =>
    p.$tag === 'DTL'       ? '#60a5fa' :
    p.$tag === 'Livermore' ? '#f59e0b' :
    p.$tag === 'Minervini' ? '#a78bfa' :
    '#34d399'};
  border: 1px solid ${p =>
    p.$tag === 'DTL'       ? 'rgba(96,165,250,0.25)'  :
    p.$tag === 'Livermore' ? 'rgba(245,158,11,0.25)'  :
    p.$tag === 'Minervini' ? 'rgba(167,139,250,0.25)' :
    'rgba(52,211,153,0.25)'};
`;

/* ── 하단 정보 ── */
const CardBottom = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding-top: 12px; border-top: 1px solid #1e2030;
  font-size: 11px; color: #64748b;
`;
const ReturnBadge = styled.span`
  font-size: 13px; font-weight: 700;
  color: ${p => p.$val > 0 ? '#10b981' : p.$val < 0 ? '#ef4444' : '#64748b'};
  display: flex; align-items: center; gap: 3px;
`;
const DayLeft = styled.span`
  font-weight: 600;
  color: ${p => p.$days <= 7 ? '#ef4444' : p.$days <= 14 ? '#f59e0b' : '#94a3b8'};
`;

/* ── 빈 상태 ── */
const EmptyState = styled.div`
  padding: 60px 20px; text-align: center; color: #374151;
`;

/* ── 스켈레톤 ── */
const SkeletonCard = styled.div`
  background: #111827; border: 1px solid #1e2030; border-radius: 16px;
  padding: 18px; height: 240px;
  background: linear-gradient(90deg, #111827 25%, #1e2030 50%, #111827 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s infinite linear;
`;

// ─── 컴포넌트 ─────────────────────────────────────────────────────────────────
function DtlFocusListPage() {
  const navigate = useNavigate();

  const [data,      setData]      = useState(null);   // Firestore 저장 결과
  const [loading,   setLoading]   = useState(true);
  const [running,   setRunning]   = useState(false);
  const [progress,  setProgress]  = useState(null);   // { current, total, symbol, name }
  const [error,     setError]     = useState(null);

  // ── 저장된 결과 로드 ────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getDtlFocusList();
      setData(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── 포커스 리스트 산출 (SSE) ────────────────────────────────────────────────
  const handleRun = async () => {
    setRunning(true);
    setProgress({ current: 0, total: 0, symbol: '', name: '준비 중...' });
    setError(null);

    try {
      await apiService.runDtlFocusListStream((msg) => {
        if (msg.type === 'progress') {
          setProgress({ current: msg.current, total: msg.total, symbol: msg.symbol, name: msg.name });
        } else if (msg.type === 'done') {
          // stocks는 Firestore에서 별도 조회 (done 페이로드에서 제거됨)
          loadData();
          setProgress(null);
        } else if (msg.type === 'error') {
          setError(msg.error);
          setProgress(null);
        }
      });
    } catch (e) {
      setError(e.message);
      setProgress(null);
    } finally {
      setRunning(false);
    }
  };

  const fmt = n => n != null ? Number(n).toLocaleString() : '-';
  const fmtDate = iso => iso ? iso.slice(0, 16).replace('T', ' ') : '-';

  const stocks = data?.stocks || [];
  const pct = progress && progress.total > 0
    ? Math.round(progress.current / progress.total * 100)
    : 0;

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs/dtl-screener/watchlist')}>
          <ArrowLeft size={18} />
        </BackBtn>
        <TitleArea>
          <PageTitle>Focus List</PageTitle>
          <PageSub>워치리스트 멀티전략 교차검증 — 상위 10종목 중점 모니터링</PageSub>
        </TitleArea>
        <RunBtn onClick={handleRun} disabled={running}>
          <SpinAnim $active={running}><RefreshCw size={14} /></SpinAnim>
          {running ? '산출 중...' : '포커스 리스트 갱신'}
        </RunBtn>
      </TopBar>

      {/* ── 메타 정보 ── */}
      {data && !running && (
        <MetaBar>
          <MetaItem>마지막 갱신 <MetaVal>{fmtDate(data.updatedAt)}</MetaVal></MetaItem>
          <MetaItem>워치리스트 <MetaVal>{data.total}종목</MetaVal></MetaItem>
          <MetaItem>스코어 산출 <MetaVal>{data.scored}종목</MetaVal></MetaItem>
          <MetaItem style={{ marginLeft: 'auto', fontSize: 11, color: '#374151' }}>
            가중치: DTL 30% · Livermore 30% · Minervini 20% · VWAP 20%
          </MetaItem>
        </MetaBar>
      )}

      {/* ── 진행 상황 ── */}
      {running && progress && (
        <ProgressSection>
          <ProgressLabel>
            분석 중... {progress.current}/{progress.total}
          </ProgressLabel>
          <ProgressBar>
            <ProgressFill $pct={pct} />
          </ProgressBar>
          <ProgressStock>
            {progress.name} ({progress.symbol})
          </ProgressStock>
        </ProgressSection>
      )}

      {/* ── 오류 ── */}
      {error && (
        <div style={{ padding: '12px 16px', marginBottom: 16, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, color: '#ef4444', fontSize: 13 }}>
          오류: {error}
        </div>
      )}

      {/* ── 카드 그리드 ── */}
      {loading ? (
        <Grid>
          {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
        </Grid>
      ) : stocks.length === 0 ? (
        <EmptyState>
          <div style={{ fontSize: 40, marginBottom: 14 }}>
            <Star size={40} color="#374151" />
          </div>
          <div style={{ fontSize: 14 }}>포커스 리스트가 없습니다.</div>
          <div style={{ fontSize: 12, marginTop: 8, color: '#1e2030' }}>
            워치리스트에 종목을 추가한 뒤 갱신 버튼을 눌러주세요.
          </div>
        </EmptyState>
      ) : (
        <Grid>
          {stocks.map(stock => (
            <StockCard key={stock.symbol} stock={stock} fmt={fmt} />
          ))}
        </Grid>
      )}
    </Page>
  );
}

// ─── 단일 카드 ────────────────────────────────────────────────────────────────
function StockCard({ stock, fmt }) {
  const {
    rank, symbol, name, market, focusScore,
    dtlScore, livermoreScore, minerviniScore, vwapScore,
    tags, returnPct, dDay, remainingTradingDays, dDayClose, currentClose
  } = stock;

  const ReturnIcon = returnPct > 0 ? TrendingUp : returnPct < 0 ? TrendingDown : Minus;

  return (
    <Card $rank={rank}>
      <CardTop>
        <RankBadge $rank={rank}>{rank}</RankBadge>
        <StockInfo>
          <StockName>{name}</StockName>
          <StockMeta>
            <StockCode>{symbol}</StockCode>
            <MarketTag $market={market}>{market}</MarketTag>
          </StockMeta>
        </StockInfo>
        <ScoreCircle $score={focusScore}>
          <ScoreNum $score={focusScore}>{focusScore}</ScoreNum>
          <ScoreLabel>FOCUS</ScoreLabel>
        </ScoreCircle>
      </CardTop>

      {/* 서브 스코어 바 */}
      <SubScores>
        <ScoreRow>
          <ScoreName>DTL</ScoreName>
          <ScoreBar><ScoreBarFill $pct={dtlScore} $color="dtl" /></ScoreBar>
          <ScoreVal>{dtlScore}</ScoreVal>
        </ScoreRow>
        <ScoreRow>
          <ScoreName>Livermore</ScoreName>
          <ScoreBar><ScoreBarFill $pct={livermoreScore} $color="livermore" /></ScoreBar>
          <ScoreVal>{livermoreScore}</ScoreVal>
        </ScoreRow>
        <ScoreRow>
          <ScoreName>Minervini</ScoreName>
          <ScoreBar><ScoreBarFill $pct={minerviniScore} $color="minervini" /></ScoreBar>
          <ScoreVal>{minerviniScore}</ScoreVal>
        </ScoreRow>
        <ScoreRow>
          <ScoreName>VWAP</ScoreName>
          <ScoreBar><ScoreBarFill $pct={vwapScore} $color="vwap" /></ScoreBar>
          <ScoreVal>{vwapScore}</ScoreVal>
        </ScoreRow>
      </SubScores>

      {/* 전략 태그 */}
      {tags && tags.length > 0 && (
        <TagRow>
          {tags.map(t => <Tag key={t} $tag={t}>{t}</Tag>)}
        </TagRow>
      )}

      {/* 하단: 등재일 수익, 잔여일 */}
      <CardBottom>
        <div>
          <div style={{ fontSize: 10, color: '#475569', marginBottom: 2 }}>
            등재가 {fmt(dDayClose)} → {fmt(currentClose)}
          </div>
          <ReturnBadge $val={returnPct}>
            <ReturnIcon size={12} />
            {returnPct != null ? `${returnPct > 0 ? '+' : ''}${returnPct}%` : '-'}
          </ReturnBadge>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, color: '#475569', marginBottom: 2 }}>
            {dDay ? `등재 ${dDay.slice(5)}` : ''}
          </div>
          <DayLeft $days={remainingTradingDays}>
            잔여 {remainingTradingDays}일
          </DayLeft>
        </div>
      </CardBottom>
    </Card>
  );
}

export default DtlFocusListPage;
